import { defineComponent } from 'vue';

import '../../prototypeExtensions/String';
import API from '../API';
import toaster from '../components/common/toaster';
import { delay } from '../utility';

export default defineComponent({
    data() {
        return {

            /** The following data is implemented in entriesCommonMixin.js
             * entriesMap: {},
             * renderedEntriesMap: {},
             * fieldValuesMap: {},
             */

            /** The following data must be implemented by the mixin-implementing component
             * @type {Record.<string, {matchMode: string, value: string[], num?: true, fieldName?: string}>}
             * filters: {}
             *
             * @type {string}
             * t_data_load_error: "Failed to load entries!"
             * */

            /** */
            loading: true,

            loadTries: 0,

            /** Whether filtering is performed as a boolean AND operation (when true) or as an OR operation (when false) */
            filtersMatchAll: false,

            /** Whether hidden entries should be displayed */
            hiddenEntriesDisplayed: false,

            /** Whether loadData returned no entries from backend */
            noEntriesFromBackend: false,

            /** Whether any of the loaded entries are 'hidden' (have the '§hidden' tag) */
            anyEntriesHidden: false,

            /** Whether any of the loaded entries are 'imported' (have the '§imported' tag) */
            anyEntriesImported: false,

            /** Which fields of the loaded entries will be checked for URL encoded characters and have them replaced */
            fieldNamesToCheckForUrlEncodedCharacters: ['name', 'usergroups', 'tags', 'project']
        };
    },
    computed: {
        filtersEmpty() {
            return !Object.values(this.filters).some(filterEntry => filterEntry.value && filterEntry.value.length > 0);
        },
        entriesWithOptionalHidden() {
            let listReference = this.hiddenEntriesDisplayed ? this.entries : this.entries.filter(entry => !entry._hidden);

            /** Extra filtering function used in dragdrop.vue tabItems to filter elements based on currently
             * selected layer and usergroups */
            if(this.elementLayerUsergroupFilteringFunction) {
                listReference = listReference.filter(this.elementLayerUsergroupFilteringFunction);
            }

            return listReference;
        }
    },
    created() {
        EaseEventBus.listen('tagSearchUpdated', tagSearch => {
            this.filtersMatchAll = !tagSearch;
            setTimeout(() => {
                if(!this.renderedEntries?.length) {
                    this.clearFilters();
                }
            }, 5);
        });

        this.filtersMatchAll = !Laravel.tag_search;
        // this.loadUserData();
    },
    methods: {
        async loadData(getData, fieldsToKeep) {
            this.loading = true;

            let dataResponse;
            try {
                dataResponse = getData instanceof Function ? await getData() : { data: getData };
            } catch (error) {
                //console.log('error', error);

                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                if(++this.loadTries >= 3) {
                    toaster.error(this.t_data_load_error);
                    dataResponse = { error };
                    this.loadTries = 0;
                } else {
                    await delay(350);
                    return this.loadData(getData, fieldsToKeep);
                }
            }

            this.noEntriesFromBackend = !Array.isArray(dataResponse.data) || dataResponse.data.length === 0;

            //const subFieldsToKeep = {
            //    usergroups: ['id', 'name']
            //}

            const tempEntriesMap = {};
            const tempEntriesArray = [];

            //console.log('dataResponse', dataResponse)

            let tempAnyEntriesHidden = false;
            let tempAnyEntriesImported = false;

            dataResponse?.data?.forEach(entry => {
                //console.log('entry', entry)
                const cleanEntry = {};
                (fieldsToKeep ? fieldsToKeep : Object.keys(entry)).forEach(fieldName => {
                    /*const subFields = subFieldsToKeep[fieldName]
                    let cleanSubEntry
                    if (subFields && !isArrayEmpty(entry[fieldName])) {
                        //console.log('subFields for', entry[fieldName], subFields)

                        entry[fieldName].forEach(subEntry => {
                            console.log('subEntry', subEntry)

                            const subEntryArray = []
                            subFields.forEach(subField => {
                                //console.log('assigning', subEntry[subField])

                                subEntryArray.push(subEntry[subField])
                            })
                            cleanSubEntry = subEntryArray
                        })
                    } else {
                        cleanSubEntry = entry[fieldName]
                    }
                    cleanEntry[fieldName] = entry[fieldName] //cleanSubEntry
                    */

                    const actualFieldName = this.filters[fieldName]?.fieldName ?? fieldName;

                    if (this.fieldNamesToCheckForUrlEncodedCharacters.includes(fieldName)) {
                        if(Array.isArray(entry[actualFieldName])) {
                            cleanEntry[fieldName] = entry[actualFieldName].map(arrayEntry => ({
                                ...arrayEntry,
                                name: arrayEntry.name.replaceAmpGtLt()
                            }));
                        } else {
                            //console.log('actualFieldName:', actualFieldName, ', entry[actualFieldName]:', entry[actualFieldName])
                            cleanEntry[fieldName] = entry[actualFieldName].replaceAmpGtLt();
                        }
                    } else {
                        cleanEntry[fieldName] = entry[actualFieldName];
                    }
                });
                const entryHidden = entry.tags.some(tag => tag.name === '§hidden');
                cleanEntry._hidden = entryHidden;
                const entryimported = entry.tags.some(tag => tag.name === '§imported');
                cleanEntry._imported = entryimported;

                if (entryHidden) {
                    tempAnyEntriesHidden = true;
                }
                if (entryimported) {
                    tempAnyEntriesImported = true;
                }

                tempEntriesMap[cleanEntry.id] = cleanEntry;
                tempEntriesArray.push(cleanEntry);

            });

            this.anyEntriesHidden = tempAnyEntriesHidden;
            this.anyEntriesImported = tempAnyEntriesImported;

            const tempFieldsMap = {};

            const fieldNames = Object.keys(this.filters);

            fieldNames.forEach((fieldName, index) => {
                const isFieldArray = this.filters[fieldName].matchMode.startsWith('array');
                const isFieldValueNumeric = !!this.filters[fieldName].num;

                const tempFieldEntriesArray = [];

                tempEntriesArray.forEach(tempEntry => {
                    if (isFieldArray) {
                        tempEntry[fieldName].forEach(fieldEntry => tempFieldEntriesArray.push({
                            value: fieldEntry.name,
                            id: tempEntry.id
                        }));
                    } else {
                        tempFieldEntriesArray.push({
                            value: tempEntry[fieldName],
                            id: tempEntry.id
                        });
                    }
                });

                if (isFieldValueNumeric) {
                    const numSortedArray = tempFieldEntriesArray.sort((a, b) => a.value - b.value);
                    numSortedArray.forEach(fieldEntry => fieldEntry.value = String(fieldEntry.value));
                    tempFieldsMap[fieldName] = numSortedArray;
                } else {
                    tempFieldsMap[fieldName] = tempFieldEntriesArray;
                }

                //console.log('tempFieldEntriesArray', tempFieldEntriesArray)
            });

            this.entriesMap = { ...tempEntriesMap };

            if (tempFieldsMap.screen_info) {
                /** @type {Set<string>} */
                const screenInfoValuesSet = new Set(Object.values(tempFieldsMap.screen_info).map(screenInfo => screenInfo.value));
                const valueRatioArray = Array.from(screenInfoValuesSet).map(screenInfoValue => ({
                        value: screenInfoValue,
                        ratio: screenInfoValue
                            .substring(screenInfoValue.indexOf('| ') + 2)
                            .replaceAll('NaN', '1')
                            .split(':')
                            .reduce((widthRatio, heightRatio) => +widthRatio / +heightRatio)
                    })
                );
                valueRatioArray.sort((a, b) => b.ratio - a.ratio);

                const sortedValueRatiosMap = {};
                valueRatioArray.forEach((data, index) => sortedValueRatiosMap[data.value] = index);

                tempFieldsMap.screen_info.sort((a, b) => sortedValueRatiosMap[a.value] - sortedValueRatiosMap[b.value]);
            }

            this.fieldValuesMap = { ...tempFieldsMap };

            //console.log('this.entriesMap', this.entriesMap)
            //console.log('this.fieldValuesMap', this.fieldValuesMap)

            this.loading = false;

            return dataResponse;
        },
        async loadUserData() {
            const url = '/administration/myuser/edit/data';
            const api = new API(url);

            const data = await api.get();

            this.filtersMatchAll = !(+data.tagSearch || 0);
        },
        filteredItemsWithUserEntered(fieldEntriesArray, userEnteredItemsArray, skipArraySort = false) {
            if (!fieldEntriesArray) return null;

            const filteredFieldValuesArray = fieldEntriesArray.filter(fieldEntry =>
                // If filtersMatchAll, only show already rendered entries in filter dropdowns, else show all entries
                ((this.filtersMatchAll ? this.renderedEntriesMap[fieldEntry.id] : true) ||
                    (this.hiddenEntriesDisplayed ? fieldEntry.value === '§hidden' : false)) &&
                // If hidden entries are not displayed, hide the filter entries that belong to hidden elements
                (!this.hiddenEntriesDisplayed ? !this.entriesMap[fieldEntry.id]._hidden : true)
            );

            if (!skipArraySort) {
                filteredFieldValuesArray.sort((a, b) => a.value.localeCompare(b.value, Laravel.activeLanguage));
            }

            //console.log('filteredItemsWithUserEntered', filteredFieldValuesArray, userEnteredItemsArray)
            return (userEnteredItemsArray?.length > 0)
                ? filteredFieldValuesArray.concat(
                    filteredFieldValuesArray.length > 0 ? { divider: true } : [],
                    { header: 'Custom values' },
                    userEnteredItemsArray.map(userEnteredTag => ({ value: userEnteredTag, userEntered: true })))
                : filteredFieldValuesArray;
        },
        clearFilters() {
            Object.keys(this.filters).forEach(filterKey => this.clearFilterEntry(filterKey));
        },
        clearFilterEntry(filterKey) {
            if (!this.filters[filterKey]) return
            this.filters[filterKey].value = []
            delete this.userEnteredItemsMap[filterKey]
        },
    },
    watch: {
        filtersMatchAll(val) {
            Object.keys(this.filters)
                // Do not change the matchMode of the media Type filters (so far only in screen-mediapool.vue)
                // because they will never overlap (an element will not be both 'picture' and 'video')
                // and users will expect them to always act with OR filtering (any must match).
                .filter(key => key != 'media_type_key')
                .forEach((filterKey) => {
                    if (this.filters[filterKey].matchMode.startsWith('containsArray')) {
                        this.filters[filterKey].matchMode = val ? 'containsArray_AND' : 'containsArray_OR';
                        return;
                    }
                    if (this.filters[filterKey].matchMode.startsWith('arrayContainsNameArray')) {
                        this.filters[filterKey].matchMode = val ? 'arrayContainsNameArray_AND' : 'arrayContainsNameArray_OR';
                        return;
                    }
                });
        },
        filters: {
            handler(val) {
                this.hiddenEntriesDisplayed = val.tags?.value.includes('§hidden');
            },
            deep: true
        },
        loading(val) {
            this.$emit('loading', val);
        },
    }
});
